import { formatMetricValue } from '@utils';
import type { Metric } from 'enums';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import type { ChartData } from 'types/common';
import Styled from './HorizontalStackedBarChart.styled';

export interface HorizontalStackedBarChartProps {
  colors: readonly string[];
  data: ChartData[];
  metric?: Metric;
  renderLabel?: (item: ChartData) => string;
}

function HorizontalStackedBarChart({
  colors,
  data,
  metric,
  renderLabel
}: HorizontalStackedBarChartProps) {
  const totalValue = data.reduce((sum, segment) => sum + segment.value, 0);
  const chartMax = totalValue > 0 ? totalValue : 1;
  const chartRow = data.reduce<Record<string, number>>(
    (acc, segment, index) => {
      acc[`segment_${index}`] = segment.value;

      return acc;
    },
    {}
  );

  return (
    <Styled.Wrapper>
      <Styled.ChartArea>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[chartRow]}
            layout="vertical"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <XAxis type="number" domain={[0, chartMax]} hide />
            <YAxis type="category" dataKey="name" hide />
            {data.map((_, index) => {
              const dataKey = `segment_${index}`;
              const isFirst = index === 0;
              const isLast = index === data.length - 1;

              return (
                <Bar
                  key={dataKey}
                  dataKey={dataKey}
                  stackId="stacked"
                  fill={colors[index % colors.length]}
                  radius={[
                    isFirst ? 4 : 0,
                    isLast ? 4 : 0,
                    isLast ? 4 : 0,
                    isFirst ? 4 : 0
                  ]}>
                  <LabelList
                    dataKey={dataKey}
                    position="center"
                    fill="#fff"
                    formatter={(value: number) =>
                      formatMetricValue(value, metric)
                    }
                    style={{ fontSize: 14, fontWeight: 600, lineHeight: 16 }}
                  />
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </Styled.ChartArea>
      {renderLabel && (
        <Styled.ValueRow>
          {data.map((segment, index) => (
            <Styled.ValueItem
              key={`value-${segment.name}-${index}`}
              width={(segment.value / chartMax) * 100}>
              <Styled.ValueText>{renderLabel(segment)}</Styled.ValueText>
            </Styled.ValueItem>
          ))}
        </Styled.ValueRow>
      )}
    </Styled.Wrapper>
  );
}

export default HorizontalStackedBarChart;
